import { useTranslation } from "react-i18next";

/** "15.00" -> "15", "7.50" -> "7.5" — for the "Tax (15%)" label. */
const rateLabel = (rate) => String(Number(rate));

/**
 * Subtotal / Discount / Tax / Total, exactly as the server computed them
 * (a booking's `bill`, a payment's `breakdown`, a reward's `bill_after`). No
 * arithmetic here: every figure is read, none is derived.
 *
 * Tax reads "Tax (15%)" when one rate is added on top, "Tax" when the visit
 * mixes rates, and "Tax included" (no amount) when nothing is added.
 */
export default function BillBreakdown({ bill, currency, totalLabel, className = "" }) {
  const { t } = useTranslation();

  if (!bill) return null;

  const money = (value) => (
    <span dir="ltr" className="tabular-nums">
      {value} {currency?.code ?? ""}
    </span>
  );

  const taxed = Number(bill.tax_amount) > 0;

  return (
    <dl className={`space-y-1 text-sm ${className}`}>
      <div className="flex justify-between">
        <dt className="text-ink-2">{t("bill.subtotal")}</dt>
        <dd>{money(bill.subtotal)}</dd>
      </div>
      {Number(bill.discount) > 0 && (
        <div className="flex justify-between">
          <dt className="text-ink-2">{t("bill.discount")}</dt>
          <dd className="text-accent-text">− {money(bill.discount)}</dd>
        </div>
      )}
      <div className="flex justify-between">
        <dt className="text-ink-2">
          {taxed
            ? bill.tax_rate
              ? t("bill.taxRate", { rate: rateLabel(bill.tax_rate) })
              : t("bill.tax")
            : t("bill.taxIncluded")}
        </dt>
        <dd>{taxed ? money(bill.tax_amount) : "—"}</dd>
      </div>
      <div className="flex justify-between border-t border-line pt-1 font-medium">
        <dt>{totalLabel ?? t("bill.total")}</dt>
        <dd>{money(bill.total)}</dd>
      </div>
    </dl>
  );
}
